import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Loader2, Shield } from 'lucide-react';
import { completeDelegatedConnection } from '../services/connectionService';

/**
 * OAuth Callback Handler — routes Microsoft responses to the correct flow.
 *
 * Plan A (delegated): URL has `code` + `state` → exchange for tokens via backend
 * Plan B (app-only):  URL has `admin_consent=True` + `tenant` → store tenant ID, redirect
 * Error:              URL has `error` + `error_description`
 */
const Callback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('Processing Microsoft response...');

  useEffect(() => {
    handleCallback();
  }, []);

  async function handleCallback() {
    const errorParam = searchParams.get('error');
    const errorDescriptionParam = searchParams.get('error_description');
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const adminConsentParam = searchParams.get('admin_consent');
    const tenantParam = searchParams.get('tenant');

    // Error response
    if (errorParam) {
      setStatus('error');
      setErrorMessage(errorDescriptionParam || errorParam || 'Microsoft returned an error');
      return;
    }

    // Plan A: Authorization code flow
    if (code && state) {
      await handleDelegatedCallback(code, state);
      return;
    }

    // Plan B: Admin consent flow
    if (adminConsentParam === 'True') {
      handleAdminConsentCallback(tenantParam, state);
      return;
    }

    // Unexpected response
    setStatus('error');
    setErrorMessage('Unexpected response from Microsoft. Please try again.');
  }

  /**
   * Plan A: Exchange authorization code for tokens and run access test.
   */
  async function handleDelegatedCallback(code: string, state: string) {
    setStatusMessage('Exchanging authorization code...');

    // Validate state from sessionStorage (CSRF protection)
    const savedState = sessionStorage.getItem('niwt_oauth_state');
    if (!savedState || savedState !== state) {
      setStatus('error');
      setErrorMessage('OAuth state mismatch. This may be a security issue. Please restart the connection.');
      return;
    }

    try {
      setStatusMessage('Running access test...');
      const result = await completeDelegatedConnection(code, state);

      // Redirect to results screen immediately (success or failure)
      navigate(
        `/azure-setup?connectionResult=${encodeURIComponent(JSON.stringify(result))}`,
        { replace: true }
      );
    } catch (err: any) {
      setStatus('error');
      setErrorMessage(err.message || 'Failed to complete the connection');
    }
  }

  /**
   * Plan B: Admin consent granted, redirect back with tenant ID.
   */
  function handleAdminConsentCallback(tenantId: string | null, state: string | null) {
    // Redirect immediately — no artificial delay
    if (tenantId) {
      navigate(
        `/azure-setup?mode=planB&tenant=${encodeURIComponent(tenantId)}`,
        { replace: true }
      );
    } else {
      navigate('/azure-setup?consent=granted', { replace: true });
    }
  }

  const handleRetry = () => {
    navigate('/azure-setup', { replace: true });
  };

  if (status === 'processing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <Shield className="w-16 h-16 text-niwt-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing</h2>
            <p className="text-gray-600 mb-6">{statusMessage}</p>
            <div className="flex items-center justify-center space-x-2">
              <Loader2 className="w-5 h-5 text-niwt-primary animate-spin" />
              <span className="text-sm text-gray-600">This should only take a moment</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Connection Failed</h2>
          <p className="text-gray-600 mb-4">
            There was an issue with the Microsoft authorization.
          </p>
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-6">
            <p className="text-sm text-red-800">{errorMessage}</p>
          </div>
          <button
            onClick={handleRetry}
            className="w-full bg-niwt-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-niwt-primary/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
};

export default Callback;
